import Ajv from 'ajv';
import CSSModules from 'react-css-modules';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Form, Scope, asField } from 'informed';
import {
  Button,
  ButtonOutlined,
  FieldInput,
  FieldInputCheckboxToggle,
  FieldInputCurrency,
  FieldInputMask,
  FieldInputMultipleCheckboxes,
  FieldInputSelect,
  FieldInputSlider,
  FieldInputRange,
} from 'platfi-ui-components';

import styles from './styles.scss';
import { useCountryContext } from '../../hooks/useCountryContext';

// Field components
const Currency = asField(FieldInputCurrency);
const Input = asField(FieldInput);
const Mask = asField(FieldInputMask);
const MultipleCheckboxes = asField(FieldInputMultipleCheckboxes);
const Select = asField(FieldInputSelect);
const Slider = asField(FieldInputSlider);
const Range = asField(FieldInputRange);
const ToggleCheckbox = asField(FieldInputCheckboxToggle);

const FormBuilder = (props) => {
  const country = useCountryContext();
  const [hasChange, setHasChange] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    updateFormErrors(props.initialValues);
  }, []);

  const ajv = new Ajv({
    $data: true,
    allErrors: true,
    coerceTypes: true,
  });

  const getErrors = (values) => {
    ajv.validate(props.schema, values);
    return ajv.errors || [];
  };

  const renderField = (scopeName) => (fieldConfig) => {
    const {
      columns,
      field,
      type,
      label,
      options,
      optionsColumns,
      // eslint-disable-next-line no-unused-vars
      values,
      tooltip,
      ...rest
    } = fieldConfig;

    const { initialValues } = props;
    const fieldFullName = [scopeName, field].join('.');
    const fieldProps = {
      ...rest,
      field,
      name: field,
      validateOnBlur: true,
      validateOnChange: true,
      validateOnMount: true,
    };

    const schemaItems =
      props.schema?.properties?.[field]?.items || props.schema?.properties?.[field] || {};

    const fieldOptions =
      (schemaItems?.enum &&
        schemaItems.enum.map((value, index) => ({
          // eslint-disable-next-line dot-notation
          label: schemaItems?.['enum_titles']?.[index] || null,
          value,
        }))) ||
      [];

    return (
      <div className={`col-xs-${columns || 6}`} key={`${fieldFullName}${props.section}`}>
        {type === 'range' && (
          <Range
            {...fieldProps}
            label={label}
            tooltipNormalizer={(value) => `${value} ${tooltip || ''}`}
            fields={Object.keys(initialValues[field])}
          />
        )}

        {(type === 'text' || type === 'number') && (
          <Input {...fieldProps} label={label} type={type} />
        )}

        {type === 'textarea' && (
          <Input {...fieldProps} label={label} type={type} isTextArea={type} />
        )}

        {type === 'mask' && <Mask {...fieldProps} label={label} options={options} />}

        {type === 'currency' && (
          <Currency
            {...fieldProps}
            currencySymbol={country.currency?.symbol}
            label={label}
            normalize={Number}
            type={type}
          />
        )}

        {type === 'boolean' && <ToggleCheckbox {...fieldProps} />}

        {type === 'multiple' && (
          <MultipleCheckboxes
            {...fieldProps}
            label={label}
            options={fieldOptions}
            listClassname={classNames(styles['form-builder__list'], {
              'row row-flex': optionsColumns,
            })}
            listItemClassname={classNames({ [`col-xs-${optionsColumns || 12}`]: optionsColumns })}
          />
        )}

        {type === 'slider' && (
          <Slider
            {...fieldProps}
            label={label}
            tooltipNormalizer={(value) => `${value} ${tooltip || ''}`}
          />
        )}

        {type === 'select' && <Select {...fieldProps} label={label} options={fieldOptions} />}
      </div>
    );
  };

  const handleOnSubmit = (values) => {
    const { schema } = props;
    const errors = getErrors(values);

    if (errors.length) {
      updateFormErrors(values);
      return;
    }

    props.onSubmit(values, schema.title);
  };

  const handleOnChange = (values) => {
    const { initialValues, formChange } = props;
    JSON.stringify(initialValues) !== JSON.stringify(values) && setHasChange(true);
    updateFormErrors(values);
    formChange && formChange(props.section, false);
  };

  const updateFormErrors = (values) => {
    if (!formRef.current) {
      return;
    }
    const { controller } = formRef.current;
    const { fieldExists, setError } = controller.api;
    if (controller) {
      controller.errors.rebuild();
      controller.emit('update', controller.state);

      // handles error in lib that renders errors in first render
      getErrors(values);

      getErrors(values).forEach(({ dataPath, message }) => {
        const fieldName = dataPath.substring(1);

        if (fieldExists(fieldName)) {
          setError(fieldName, message);
        }
      });
    }
  };

  const renderFieldset = (fieldset) => {
    const { noBorder, noMargin } = props;
    const { noBorder: noBorderFieldset, columns, label, field, fields } = fieldset;
    const renderFi = renderField(field);
    const borderModifier = `form-builder__fieldset--no-border${
      noBorderFieldset ? '-fieldset' : ''
    }`;
    const marginModifier = 'form-builder__fieldset--no-margin';
    const borderStyle = noBorder || noBorderFieldset ? borderModifier : false;

    return (
      <div className={`col-xs-${columns || 6}`} key={field}>
        <fieldset
          className={`row ${styles['form-builder__fieldset']} ${
            borderStyle && styles[borderStyle]
          } ${noMargin && styles[marginModifier]}`}
        >
          {label && (
            <legend className={`col-xs-12 ${styles['form-builder__label']}`}>{label}</legend>
          )}

          {field ? (
            <Scope key={`${field}${props.section}`} scope={field}>
              {fields.map(renderFi)}
            </Scope>
          ) : (
            fields.map(renderFi)
          )}
        </fieldset>
      </div>
    );
  };

  return (
    <Form
      className={`container-fluid ${styles['form-builder']}`}
      {...props}
      onSubmit={handleOnSubmit}
      onValueChange={handleOnChange}
      ref={formRef}
    >
      <div className="row row-flex">{props.uiSchema.map(renderFieldset)}</div>

      <div className={styles['form-builder__actions']}>
        {props.children || (
          <>
            <ButtonOutlined
              className={styles['form-builder__actions__button']}
              onClick={props.onReset}
              secondary
              type="reset"
            >
              Cancel
            </ButtonOutlined>

            <Button
              className={styles['form-builder__actions__button']}
              disabled={!hasChange}
              primary
              type="submit"
            >
              Save
            </Button>
          </>
        )}
      </div>
    </Form>
  );
};

FormBuilder.propTypes = {
  initialValues: PropTypes.object,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.array.isRequired,
  section: PropTypes.string,
  formChange: PropTypes.func,
  children: PropTypes.node,
  noBorder: PropTypes.bool,
  noMargin: PropTypes.bool,
};

export default CSSModules(FormBuilder, styles);
